import React, { useCallback } from "react";
import { Button } from "../../../../design/button";
import { Input } from "../../../../design/input";
import { Textarea } from "../../../../design/textarea";
import { Select } from "../../../../design/select";
import { Checkbox, CheckboxField } from "../../../../design/checkbox";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  Description,
} from "../../../../design/fieldset";
import { Divider } from "../../../../design/divider";
import { CheckBadgeIcon } from "@heroicons/react/20/solid";
import {
  WizardFormData,
  REASON_FOR_SUPERVISION_OPTIONS,
  SERVICE_TYPE_OPTIONS,
  EX_PARTE_STATUS_OPTIONS,
} from "./types";

interface BasicsStepProps {
  formData: WizardFormData;
  setFormData: React.Dispatch<React.SetStateAction<WizardFormData>>;
  onNext: () => void;
  onBack: () => void;
  extractionConfidence?: number | null;
}

const BasicsStep: React.FC<BasicsStepProps> = ({
  formData,
  setFormData,
  onNext,
  onBack,
  extractionConfidence,
}) => {
  const handleCaseChange = useCallback(
    (field: keyof WizardFormData["case"]) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData((prev) => ({
          ...prev,
          case: {
            ...prev.case,
            [field]: e.target.value,
          },
        }));
      },
    [setFormData]
  );

  const handleCaseCheckboxChange = useCallback(
    (field: keyof WizardFormData["case"]) => (checked: boolean) => {
      setFormData((prev) => ({
        ...prev,
        case: {
          ...prev.case,
          [field]: checked,
        },
      }));
    },
    [setFormData]
  );

  const handleServiceTypeToggle = useCallback(
    (serviceType: string) => (checked: boolean) => {
      setFormData((prev) => {
        const currentTypes = prev.case.serviceTypes || [];
        const newTypes = checked
          ? [...currentTypes, serviceType]
          : currentTypes.filter((t) => t !== serviceType);
        return {
          ...prev,
          case: {
            ...prev.case,
            serviceTypes: newTypes,
          },
        };
      });
    },
    [setFormData]
  );

  const isValid = formData.case.identifier.trim().length >= 2;

  return (
    <div className="space-y-6">
      {/* Confidence indicator */}
      {extractionConfidence !== null && extractionConfidence !== undefined && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
          <div className="flex items-center">
            <CheckBadgeIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700 dark:text-green-300">
                AI extraction completed with {extractionConfidence}% confidence.
                Please review and edit the information below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Case Information */}
      <Fieldset>
        <FieldGroup>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field>
              <Label htmlFor="identifier">
                Case Identifier <span className="text-red-500">*</span>
              </Label>
              <Description>
                The court-assigned case number or identifier
              </Description>
              <Input
                id="identifier"
                type="text"
                placeholder='e.g. "Case 2024-001"'
                value={formData.case.identifier}
                onChange={handleCaseChange("identifier")}
              />
            </Field>

            <div>
              <Label>Service Types</Label>
              <Description>
                Select all services being provided for this case
              </Description>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SERVICE_TYPE_OPTIONS.map((option) => (
                  <CheckboxField key={option.value}>
                    <Checkbox
                      checked={(formData.case.serviceTypes || []).includes(option.value)}
                      onChange={handleServiceTypeToggle(option.value)}
                    />
                    <Label>{option.label}</Label>
                  </CheckboxField>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field>
              <Label htmlFor="reasonForSupervision">Reason for Supervision</Label>
              <Select
                id="reasonForSupervision"
                value={formData.case.reasonForSupervision}
                onChange={handleCaseChange("reasonForSupervision")}
              >
                <option value="">Select reason...</option>
                {REASON_FOR_SUPERVISION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field>
              <Label htmlFor="referralSource">Referral Source</Label>
              <Description>Who referred this case?</Description>
              <Input
                id="referralSource"
                type="text"
                placeholder="e.g. Family Court, Attorney, CPS"
                value={formData.case.referralSource}
                onChange={handleCaseChange("referralSource")}
              />
            </Field>
          </div>
        </FieldGroup>
      </Fieldset>

      <Divider />

      {/* Court/Legal Information */}
      <div>
        <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
          Legal Status
        </h3>
        <Fieldset>
          <FieldGroup>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CheckboxField>
                <Checkbox
                  checked={formData.case.isCourtOrdered}
                  onChange={handleCaseCheckboxChange("isCourtOrdered")}
                />
                <Label>Court Ordered</Label>
              </CheckboxField>

              <CheckboxField>
                <Checkbox
                  checked={formData.case.hasDV50B}
                  onChange={handleCaseCheckboxChange("hasDV50B")}
                />
                <Label>DV 50-B Order</Label>
              </CheckboxField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field>
                <Label htmlFor="exParteStatus">Ex Parte Status</Label>
                <Select
                  id="exParteStatus"
                  value={formData.case.exParteStatus}
                  onChange={handleCaseChange("exParteStatus")}
                >
                  <option value="">Select status...</option>
                  {EX_PARTE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field>
                <Label htmlFor="judgeName">Judge&apos;s Name</Label>
                <Input
                  id="judgeName"
                  type="text"
                  placeholder="e.g. Judge Smith"
                  value={formData.case.judgeName}
                  onChange={handleCaseChange("judgeName")}
                />
              </Field>
            </div>
          </FieldGroup>
        </Fieldset>
      </div>

      <Divider />

      {/* Court Address */}
      <div>
        <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
          Court Information
        </h3>
        <Fieldset>
          <FieldGroup>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field>
                <Label htmlFor="courtCounty">County</Label>
                <Input
                  id="courtCounty"
                  type="text"
                  placeholder="e.g. Los Angeles"
                  value={formData.case.courtCounty}
                  onChange={handleCaseChange("courtCounty")}
                />
              </Field>

              <Field>
                <Label htmlFor="courtBranchName">Branch Name</Label>
                <Input
                  id="courtBranchName"
                  type="text"
                  placeholder="e.g. Stanley Mosk Courthouse"
                  value={formData.case.courtBranchName}
                  onChange={handleCaseChange("courtBranchName")}
                />
              </Field>
            </div>

            <Field>
              <Label htmlFor="courtStreetAddress">Street Address</Label>
              <Input
                id="courtStreetAddress"
                type="text"
                placeholder="e.g. 111 N. Hill Street"
                value={formData.case.courtStreetAddress}
                onChange={handleCaseChange("courtStreetAddress")}
              />
            </Field>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field className="col-span-2 md:col-span-1">
                <Label htmlFor="courtCity">City</Label>
                <Input
                  id="courtCity"
                  type="text"
                  placeholder="e.g. Los Angeles"
                  value={formData.case.courtCity}
                  onChange={handleCaseChange("courtCity")}
                />
              </Field>

              <Field>
                <Label htmlFor="courtZipCode">ZIP Code</Label>
                <Input
                  id="courtZipCode"
                  type="text"
                  placeholder="e.g. 90012"
                  value={formData.case.courtZipCode}
                  onChange={handleCaseChange("courtZipCode")}
                />
              </Field>
            </div>
          </FieldGroup>
        </Fieldset>
      </div>

      <Divider />

      {/* Safety & Notes */}
      <Fieldset>
        <FieldGroup>
          <Field>
            <Label htmlFor="safetyInformation">Safety Information & Risk Factors</Label>
            <Description>
              Important safety information and risk factors for this case
            </Description>
            <Textarea
              id="safetyInformation"
              placeholder="Document any safety concerns or risk factors..."
              value={formData.case.safetyInformation}
              onChange={handleCaseChange("safetyInformation")}
              rows={3}
            />
          </Field>

          <Field>
            <Label htmlFor="notes">Case Notes</Label>
            <Description>
              Any additional information about this case
            </Description>
            <Textarea
              id="notes"
              placeholder="Add any relevant notes..."
              value={formData.case.notes}
              onChange={handleCaseChange("notes")}
              rows={3}
            />
          </Field>
        </FieldGroup>
      </Fieldset>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button type="button" plain onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={onNext} disabled={!isValid}>
          Continue to Parents
        </Button>
      </div>
    </div>
  );
};

export default BasicsStep;
